import { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import { IconArrowLeft } from './Icons';
import PasswordInput from './PasswordInput';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30;
const EXPIRES_IN_SECONDS = 10 * 60;

export default function ForgotPasswordFlow({ onSuccess, onBack }) {
  const [step, setStep] = useState('email'); // 'email' | 'reset'
  const [email, setEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState('');

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetInfo, setResetInfo] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(EXPIRES_IN_SECONDS);
  const inputRefs = useRef([]);
  const { forgotPassword, resetPassword } = useContext(AuthContext);

  useEffect(() => {
    if (step === 'reset') inputRefs.current[0]?.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step !== 'reset' || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, secondsLeft]);

  const code = digits.join('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setRequestError('');
    setRequesting(true);
    try {
      await forgotPassword(email);
      setStep('reset');
      setCooldown(RESEND_COOLDOWN);
      setSecondsLeft(EXPIRES_IN_SECONDS);
    } catch (err) {
      setRequestError(err.message);
    } finally {
      setRequesting(false);
    }
  };

  const handleChange = (i, rawValue) => {
    const value = rawValue.replace(/\D/g, '');
    if (!value) {
      const next = [...digits];
      next[i] = '';
      setDigits(next);
      return;
    }
    // Handles multi-char input in one box (autofill / fast typing)
    const chars = value.slice(0, CODE_LENGTH - i).split('');
    const next = [...digits];
    chars.forEach((c, idx) => { next[i + idx] = c; });
    setDigits(next);
    const lastIdx = Math.min(i + chars.length, CODE_LENGTH - 1);
    inputRefs.current[lastIdx]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!text) return;
    const next = Array(CODE_LENGTH).fill('');
    text.split('').forEach((c, idx) => { next[idx] = c; });
    setDigits(next);
    inputRefs.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleResend = async () => {
    setResetError('');
    setResetInfo('');
    setResending(true);
    try {
      await forgotPassword(email);
      setResetInfo('A new code has been sent to your email.');
      setCooldown(RESEND_COOLDOWN);
      setSecondsLeft(EXPIRES_IN_SECONDS);
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResending(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetInfo('');

    if (code.length !== CODE_LENGTH) {
      setResetError('Please enter the 6-digit code.');
      return;
    }
    if (newPassword.length < 8) {
      setResetError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    setResetting(true);
    try {
      const user = await resetPassword(email, code, newPassword);
      onSuccess(user);
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetting(false);
    }
  };

  const mm = Math.floor(secondsLeft / 60);
  const ss = (secondsLeft % 60).toString().padStart(2, '0');

  if (step === 'email') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>Reset Password</h1>
          <p className="otp-sub">Enter the email on your account and we'll send you a 6-digit code to reset your password.</p>
          <form onSubmit={handleRequestCode} className="auth-form">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {requestError && <div className="auth-error">{requestError}</div>}
            <button type="submit" disabled={requesting} className="btn submit">
              {requesting ? 'Sending…' : 'Send Reset Code'}
            </button>
          </form>
          {onBack && <button type="button" className="otp-back-btn" onClick={onBack}><IconArrowLeft size={14} /> Back to Sign In</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Reset Password</h1>
        <p className="otp-sub">We sent a 6-digit code to <strong>{email}</strong>. Enter it below along with your new password.</p>

        <form className="auth-form" onSubmit={handleResetSubmit}>
          <div className="otp-digit-row" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className={`otp-digit-box${resetError ? ' otp-digit-box-err' : ''}`}
                disabled={resetting}
                aria-label={`Digit ${i + 1} of reset code`}
              />
            ))}
          </div>

          <p className="otp-expiry">
            {secondsLeft > 0
              ? <>Code expires in <strong>{mm}:{ss}</strong></>
              : <span className="otp-expired">Code expired — please resend.</span>}
          </p>

          <PasswordInput
            placeholder="New password (min 8 characters)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <PasswordInput
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          {resetError && <div className="auth-error">{resetError}</div>}
          {resetInfo && <div className="otp-info">{resetInfo}</div>}

          <button type="submit" disabled={resetting} className="btn submit">
            {resetting ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>

        <button type="button" className="otp-resend-btn" onClick={handleResend} disabled={resending || cooldown > 0}>
          {cooldown > 0 ? `Resend code in ${cooldown}s` : resending ? 'Sending…' : "Didn't get a code? Resend"}
        </button>
        {onBack && <button type="button" className="otp-back-btn" onClick={onBack}><IconArrowLeft size={14} /> Back to Sign In</button>}
      </div>
    </div>
  );
}
